import { Controller } from "@hotwired/stimulus"

const REDUCED = "(prefers-reduced-motion: reduce)"

/**
 * The notification tray filling up, one arrival at a time, with the unread
 * badge counting along. Loops with a long pause so it reads as activity rather
 * than a spinner.
 */
export default class extends Controller {
  static targets = ["item", "badge"]

  static values = {
    stagger: { type: Number, default: 340 },
    hold: { type: Number, default: 4200 }
  }

  connect() {
    if (window.matchMedia(REDUCED).matches) return

    this.cancelled = false
    this.visible = true

    this.observer = new IntersectionObserver(
      ([entry]) => (this.visible = entry.isIntersecting),
      { threshold: 0, rootMargin: "-15% 0px -15% 0px" }
    )
    this.observer.observe(this.element)

    this.reset()
    this.run()
  }

  disconnect() {
    this.cancelled = true
    if (this.observer) this.observer.disconnect()
  }

  async run() {
    while (!this.cancelled) {
      await this.idleUntilVisible()
      if (this.cancelled) return

      for (let i = 0; i < this.itemTargets.length; i++) {
        if (this.cancelled) return
        this.itemTargets[i].classList.add("is-in")
        this.setBadge(i + 1)
        await this.sleep(this.staggerValue)
      }

      await this.sleep(this.holdValue)
      this.reset()
      await this.sleep(600)
    }
  }

  setBadge(count) {
    if (!this.hasBadgeTarget) return
    this.badgeTarget.textContent = String(count)
    this.badgeTarget.classList.remove("is-bumped")
    void this.badgeTarget.offsetWidth
    this.badgeTarget.classList.add("is-bumped")
  }

  reset() {
    this.itemTargets.forEach((el) => el.classList.remove("is-in"))
    if (this.hasBadgeTarget) {
      this.badgeTarget.textContent = "0"
      this.badgeTarget.classList.remove("is-bumped")
    }
  }

  async idleUntilVisible() {
    while (!this.visible && !this.cancelled) await this.sleep(300)
  }

  sleep(ms) {
    return new Promise((resolve) => {
      const id = setTimeout(resolve, ms)
      if (this.cancelled) {
        clearTimeout(id)
        resolve()
      }
    })
  }
}
